import { useCallback, useEffect, useRef, useState } from 'react';

/** ~12 ms por letra (§4): rápido o bastante para não impacientar, lento o bastante para se ver. */
export const MS_PER_CHAR = 12;

export interface TypewriterOptions {
  /** Milissegundos por letra; `0` revela tudo de uma vez (testes e «reduzir movimento»). */
  msPerChar?: number;
}

export interface Typewriter {
  /** O trecho já «digitado». */
  shown: string;
  /** `true` quando o texto inteiro está na tela. */
  done: boolean;
  /** Conclui na hora — o clique na bolha. */
  finish: () => void;
}

/**
 * Digita `text` letra por letra.
 *
 * A resposta chega **inteira** por REST (sem streaming nesta rodada, §4) e a
 * tela a digita — é o ritmo de uma conversa, não o de um JSON. Texto novo
 * recomeça do zero; `null` limpa. `finish` mostra tudo: quem já leu não
 * espera.
 */
export function useTypewriter(text: string | null, options: TypewriterOptions = {}): Typewriter {
  const { msPerChar = MS_PER_CHAR } = options;
  const [count, setCount] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const length = text?.length ?? 0;

  const stop = useCallback(() => {
    if (timer.current !== null) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => {
    stop();
    if (!text || msPerChar <= 0) {
      setCount(text?.length ?? 0);
      return;
    }
    setCount(0);
    timer.current = setInterval(() => {
      setCount((current) => {
        if (current + 1 >= text.length) {
          stop();
          return text.length;
        }
        return current + 1;
      });
    }, msPerChar);
    return stop;
  }, [text, msPerChar, stop]);

  const finish = useCallback(() => {
    stop();
    setCount(length);
  }, [length, stop]);

  const shown = text ? text.slice(0, Math.min(count, text.length)) : '';
  return { shown, done: !text || count >= text.length, finish };
}
